import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, ImageList, ImageListItem, LinearProgress, MenuItem, Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  AddPhotoAlternate, ArrowBack, ArrowForward, CheckCircle, ErrorOutline, RadioButtonUnchecked, Send, Undo,
} from '@mui/icons-material';
import listingService from '../services/listingService';
import ListingStatusBanner from '../components/listings/ListingStatusBanner';
import DossierAccordion from '../components/listings/DossierAccordion';
import VerificationPanel from '../components/listings/VerificationPanel';
import HomeDetailsFields from '../components/listings/HomeDetailsFields';
import { brand, text } from '../theme/colors';

const INK = '#1e293b';
const TEAL_DEEP = '#134e4a';
const AMBER = '#9a3412';
const RED = '#b91c1c';
const EDGE = '#b9cbc7';

// The dossier is the realtor's until Eqaya signs it off; buyer-facing details
// stay editable on a live listing (changing a verified fact re-verifies).
const DOSSIER_EDITABLE = ['draft', 'changes_requested'];
const DETAILS_EDITABLE = ['draft', 'changes_requested', 'verified', 'published', 'unpublished'];
const WITHDRAWABLE = ['draft', 'submitted', 'changes_requested', 'desk_approved', 'verification_pending', 'verified', 'published', 'unpublished'];
const VERIFICATION_STATUSES = ['desk_approved', 'verification_pending', 'verified', 'published', 'unpublished', 'sold'];
const REVERIFY_LABELS = {
  deed_number: 'deed number', diagram_number: 'diagram number', stand_size_sqm: 'stand size',
  seller_name_on_deed: 'seller name', price_usd: 'price', cession_stand_number: 'stand number', cession_township: 'township',
};
const HOME_DETAIL_LABELS = {
  bedrooms: 'Bedrooms', bathrooms: 'Bathrooms', floor_area_sqm: 'Floor area', pin: 'Map pin',
  cession_stand_number: 'Stand number', cession_township: 'Township',
};
const STANDING_HINT = 'Restore your agency\'s EAC standing before changing verified details';

/**
 * The listing flow, one step per job (owner, 2026-09-26: "a lot of repetition
 * and duplication", "things missing like mandate to sell"). Each fact is asked
 * once — in its checklist item — and the server copies it to the listing.
 * Checklist items per step; the ownership item swaps with the tenure.
 */
const STEPS = [
  { key: 'property', title: 'The property', blurb: 'Where it is and how the seller holds it.' },
  { key: 'ownership', title: 'Ownership and mandate', blurb: 'Proof the seller owns it, and their instruction to you to sell.', items: (tenure) => [tenure === 'cession' ? 'cession' : 'title_deed', 'mandate_to_sell'] },
  { key: 'land', title: 'Council and land', blurb: 'Rates, zoning, survey, services and environment.', items: () => ['local_authority', 'survey_beacons', 'utilities', 'environmental', 'approved_building_plans', 'occupancy_or_compliance_cert'] },
  { key: 'legal', title: 'Conveyancer and price', blurb: 'Who does the transfer, and the asking price with comparables.', items: () => ['legal', 'valuation'] },
  { key: 'buyers', title: 'What buyers see', blurb: 'Title, description, home details and photos.' },
  { key: 'review', title: 'Review and submit', blurb: 'What is left, then Eqaya’s checks.' },
];
const STEP_KEYS = STEPS.map((s) => s.key);

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const hasPin = (loc) => Number.isFinite(Number(loc?.lat)) && Number.isFinite(Number(loc?.lng)) && loc?.lat !== '' && loc?.lat != null && !(Number(loc.lat) === 0 && Number(loc.lng) === 0);

/** What still stops a submit on the property / buyers steps (mirrors the server's gate). */
function detailsGaps(listing, form) {
  const gaps = { property: [], buyers: [] };
  if (!form.location?.suburb && !form.location?.city) gaps.property.push('Suburb or city');
  if (!hasPin(form.location)) gaps.property.push('Map pin');
  if (form.tenureType === 'cession') {
    if (!form.cessionStandNumber) gaps.property.push('Stand number');
    if (!form.cessionTownship) gaps.property.push('Township');
  }
  if (listing.listing_type === 'house') {
    if (!(Number(form.bedrooms) >= 1)) gaps.buyers.push('Bedrooms');
    if (!(Number(form.bathrooms) >= 1)) gaps.buyers.push('Bathrooms');
    if (!(Number(form.floorAreaSqm) > 0)) gaps.buyers.push('Floor area');
  }
  if (!form.title?.trim()) gaps.buyers.push('Title');
  return gaps;
}

// flexShrink 0: next to a two-line step title the row squeezed the badge into an oval.
const ICON_SX = { fontSize: 26, flexShrink: 0 };
function StepIcon({ state, n }) {
  if (state === 'done') return <CheckCircle sx={{ ...ICON_SX, color: TEAL_DEEP }} />;
  if (state === 'attention') return <ErrorOutline sx={{ ...ICON_SX, color: RED }} />;
  if (state === 'current') return <Box sx={{ width: 26, height: 26, minWidth: 26, flexShrink: 0, borderRadius: '50%', bgcolor: TEAL_DEEP, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{n}</Box>;
  return <RadioButtonUnchecked sx={{ ...ICON_SX, color: '#94a3b8' }} />;
}

const RealtorListingEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [warning, setWarning] = useState('');
  const [confirm, setConfirm] = useState(null); // 'submit' | 'withdraw'
  const [photoProgress, setPhotoProgress] = useState(null);

  const hydrate = (l) => {
    setListing(l);
    setForm({
      title: l.title || '', description: l.description || '', priceUsd: l.price_usd ?? '', standSizeSqm: l.stand_size_sqm ?? '',
      tenureType: l.tenure_type || 'title_deed', cessionStandNumber: l.cession_stand_number || '', cessionTownship: l.cession_township || '',
      bedrooms: l.bedrooms ?? '', bathrooms: l.bathrooms ?? '', floorAreaSqm: l.floor_area_sqm ?? '',
      yearBuilt: l.year_built ?? '', parking: l.parking || '', features: l.features || [],
      location: { address: '', suburb: '', city: '', province: '', ...(l.location || {}) },
    });
  };
  const load = useCallback(async () => {
    try { setLoading(true); const r = await listingService.get(id); hydrate(r.data); }
    catch (e) { setError(e.response?.data?.error || 'Failed to load listing'); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (location.state?.notice) {
      setNotice(location.state.notice);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const editable = listing && DOSSIER_EDITABLE.includes(listing.status);
  const detailsEditable = listing && DETAILS_EDITABLE.includes(listing.status);
  const live = detailsEditable && !editable;
  const standingBlocked = listing?.status === 'unpublished' && listing?.unpublish_reason === 'eac_standing';

  // Per-step state for the sidebar: done / attention / todo.
  const stepState = useMemo(() => {
    if (!listing) return {};
    const items = listing.dossier?.items || [];
    const gaps = detailsGaps(listing, form);
    const out = {};
    STEPS.forEach((s) => {
      if (s.items) {
        const keys = s.items(form.tenureType);
        const mine = items.filter((i) => keys.includes(i.key));
        // Optional items never hold a step back; only required ones count.
        if (mine.some((i) => ['flagged', 'rejected'].includes(i.status) || (i.expiredFields || []).length)) out[s.key] = 'attention';
        else if (mine.filter((i) => i.required).every((i) => ['approved', 'pending'].includes(i.status))) out[s.key] = 'done';
        else out[s.key] = 'todo';
      } else if (s.key === 'property') out[s.key] = gaps.property.length ? 'todo' : 'done';
      else if (s.key === 'buyers') out[s.key] = gaps.buyers.length || !(listing.photos || []).length ? 'todo' : 'done';
      else out[s.key] = ['published'].includes(listing.status) ? 'done' : 'todo';
    });
    return out;
  }, [listing, form]);

  // A draft opens on its first unfinished step; anything further along opens on Review.
  const defaultStep = useMemo(() => {
    if (!listing) return 'property';
    if (!editable) return 'review';
    return STEPS.find((s) => s.key !== 'review' && stepState[s.key] !== 'done')?.key || 'review';
  }, [listing, editable, stepState]);
  const step = STEP_KEYS.includes(params.get('step')) ? params.get('step') : defaultStep;
  const stepIdx = STEP_KEYS.indexOf(step);
  // Every step can be opened in any order. Once the realtor opens Review, steps
  // with gaps turn red so they can see where to go (owner, 2026-09-28).
  const [reviewed, setReviewed] = useState(false);
  useEffect(() => { if (step === 'review') setReviewed(true); }, [step]);
  const goto = (key) => { setParams((p) => { const n = new URLSearchParams(p); n.set('step', key); return n; }, { replace: false }); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const showError = (message) => { setNotice(''); setWarning(''); setError(message); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  /** What still holds this step back, in the words on screen. */
  const stepGaps = (key) => {
    const s = STEPS.find((x) => x.key === key);
    const gaps = detailsGaps(listing, form);
    if (key === 'property') return gaps.property;
    if (key === 'buyers') return [...gaps.buyers, ...((listing.photos || []).length ? [] : ['At least one photo'])];
    if (!s?.items) return [];
    const keys = s.items(form.tenureType);
    return (listing.dossier?.items || []).filter((i) => keys.includes(i.key) && i.required && !['approved', 'pending', 'flagged'].includes(i.status)).map((i) => i.label);
  };
  const next = () => goto(STEP_KEYS[Math.min(stepIdx + 1, STEP_KEYS.length - 1)]);
  const fail = (e, fallback) => showError(e.response?.data?.error || fallback);
  const labelsForKeys = (keys) => (keys || []).map((k) => (listing?.template?.find((t) => t.key === k) || {}).label || k).join(', ');
  const labelsForFields = (itemKey, keys) => {
    const fields = (listing?.template?.find((t) => t.key === itemKey) || {}).fields || [];
    return (keys || []).map((k) => (fields.find((f) => f.key === k) || {}).label || k).join(', ');
  };

  /** Saves the listing's own fields (property + buyer-facing). Returns true on success. */
  const saveDetails = async ({ andNext = false } = {}) => {
    try {
      setSaving(true); setError(''); setWarning('');
      const payload = {
        title: form.title, description: form.description, standSizeSqm: num(form.standSizeSqm),
        tenureType: form.tenureType, cessionStandNumber: form.cessionStandNumber || null, cessionTownship: form.cessionTownship || null,
        bedrooms: num(form.bedrooms), bathrooms: num(form.bathrooms), floorAreaSqm: num(form.floorAreaSqm), yearBuilt: num(form.yearBuilt),
        parking: form.parking, features: form.features, location: form.location,
      };
      // On a live listing the price is changed here; before that it comes from the valuation item.
      if (live) payload.priceUsd = num(form.priceUsd);
      const r = await listingService.update(id, payload);
      if (payload.tenureType !== (listing.tenure_type || 'title_deed')) {
        await load(); setNotice(`Saved. The checklist now matches a ${payload.tenureType === 'cession' ? 'cession' : 'title deed'}.`);
        if (andNext) next(); return true;
      }
      if (r.data?.reverificationRequired) {
        setWarning(`You changed ${(r.data.reasons || []).map((k) => REVERIFY_LABELS[k] || k).join(', ')}, so the listing is hidden from buyers until a new Eqaya Verification is paid for.`);
        setNotice(''); await load(); window.scrollTo({ top: 0, behavior: 'smooth' }); return true;
      }
      setListing((cur) => ({ ...cur, ...r.data })); setNotice('Saved');
      if (andNext) next();
      return true;
    } catch (e) {
      if (e.response?.data?.errorCode === 'EDIT_BLOCKED_STANDING') showError(STANDING_HINT);
      else fail(e, 'Could not save');
      return false;
    } finally { setSaving(false); }
  };
  const saveRequirement = async (key, payload) => {
    try { setError(''); await listingService.saveRequirement(id, key, payload); setNotice('Saved'); await load(); }
    catch (e) {
      const invalid = e.response?.data?.invalid;
      if (e.response?.data?.errorCode === 'MANDATE_EXPIRED' || e.response?.data?.errorCode === 'DUPLICATE_DEED_OR_DIAGRAM' || e.response?.data?.errorCode === 'NA_NOT_ALLOWED') fail(e, 'Could not save');
      else if (invalid?.length) showError(`Check these answers: ${labelsForFields(key, invalid)}`);
      else fail(e, 'Could not save');
    }
  };
  const uploadDocument = async (key, documentType, file, onProgress) => {
    try { setError(''); await listingService.uploadDocument(id, key, documentType, file, onProgress); await load(); }
    catch (e) { fail(e, 'Upload failed'); }
  };
  const deleteDocument = async (docId) => {
    try { setError(''); await listingService.deleteDocument(id, docId); await load(); } catch (e) { fail(e, 'Could not delete document'); }
  };
  const uploadPhotos = async (e) => {
    if (!e.target.files?.length) return;
    try { setError(''); setPhotoProgress(0); await listingService.uploadPhotos(id, e.target.files, setPhotoProgress); await load(); }
    catch (err) { fail(err, 'Photo upload failed'); } finally { setPhotoProgress(null); e.target.value = ''; }
  };
  const runConfirmed = async () => {
    const which = confirm; setConfirm(null);
    try {
      setError('');
      if (which === 'submit') await listingService.submit(id); else await listingService.withdraw(id);
      setNotice(which === 'submit' ? 'Submitted. Eqaya reviews the dossier next; you will get a message when it is done.' : 'Listing withdrawn');
      await load();
    } catch (e) {
      const d = e.response?.data;
      if (d?.errorCode === 'HOME_DETAILS_INCOMPLETE') showError(`Add these first: ${(d.missing || []).map((k) => HOME_DETAIL_LABELS[k] || k).join(', ')}`);
      else if (d?.errorCode === 'DOSSIER_INCOMPLETE') showError(`Complete these items first: ${labelsForKeys(d.missing)}`);
      else fail(e, 'Action failed');
    }
  };

  if (loading && !listing) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} /></Container>;
  if (!listing) return <Container sx={{ py: 4 }}><Alert severity="error">{error || 'Listing not found'}</Alert></Container>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLoc = (k) => (e) => setForm({ ...form, location: { ...form.location, [k]: e.target.value } });
  const d = listing.dossier;
  const current = STEPS[stepIdx];
  const gaps = detailsGaps(listing, form);
  const dossierItems = d.items || [];
  const blockingItems = (d.blocking || []).map((k) => ({ key: k, label: labelsForKeys([k]), step: STEPS.find((s) => s.items && s.items(form.tenureType).includes(k))?.key }));
  const isHouse = listing.listing_type === 'house';

  const saveBar = (label = 'Save and continue') => detailsEditable && (
    <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
      <Button variant="contained" disabled={saving} onClick={() => saveDetails({ andNext: true })} endIcon={<ArrowForward />}
        sx={{ height: 46, px: 3, borderRadius: '10px', bgcolor: brand.primaryDark, textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { bgcolor: TEAL_DEEP } }}>{label}</Button>
      <Button disabled={saving} onClick={() => saveDetails()} sx={{ textTransform: 'none', fontWeight: 600, color: INK }}>Save</Button>
    </Stack>
  );
  const nextBar = stepIdx < STEP_KEYS.length - 1 && (
    <Button onClick={next} endIcon={<ArrowForward />} sx={{ mt: 3, height: 46, px: 3, borderRadius: '10px', border: `1.5px solid ${brand.primaryDark}`, color: brand.primaryDark, textTransform: 'none', fontWeight: 700 }}>
      Next: {STEPS[stepIdx + 1].title}
    </Button>
  );

  return (
    <Box sx={{ bgcolor: '#f6f9f8', minHeight: '100vh' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dfe6e4' }}>
        <Container maxWidth="xl" sx={{ py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap">
            <IconButton aria-label="Back to listings" onClick={() => navigate('/realtor/listings')}><ArrowBack /></IconButton>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.primaryDark }}>
                {isHouse ? 'House' : 'Stand'} · {listing.tenure_type === 'cession' ? 'Cession' : 'Title deed'}
              </Typography>
              <Typography component="h1" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, color: INK, lineHeight: 1.2 }}>{listing.title}</Typography>
            </Box>
            <Chip label={`Checklist ${d.submittedCount}/${d.requiredCount}`} sx={{ fontWeight: 700, bgcolor: '#e6f4f2', color: TEAL_DEEP, borderRadius: '8px' }} />
            {WITHDRAWABLE.includes(listing.status) && <Button color="inherit" startIcon={<Undo />} onClick={() => setConfirm('withdraw')} sx={{ textTransform: 'none' }}>Withdraw</Button>}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <ListingStatusBanner listing={listing} />
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {warning && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning('')}>{warning}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px minmax(0, 1fr)' }, gap: 3.5, alignItems: 'start' }}>
          {/* Steps: a sidebar on desktop, a compact scroller on phones. */}
          <Box component="nav" aria-label="Listing steps"
            sx={{ position: { md: 'sticky' }, top: { md: 88 }, bgcolor: '#fff', borderRadius: 2, border: `1px solid ${EDGE}`, p: 1,
              display: { xs: 'flex', md: 'block' }, overflowX: { xs: 'auto', md: 'visible' }, gap: 0.5 }}>
            {STEPS.map((s, i) => {
              const active = s.key === step;
              // After Review has been opened, an unfinished step is flagged like a sent-back one.
              const gapsHere = reviewed && editable && s.key !== 'review' && stepState[s.key] === 'todo' ? stepGaps(s.key) : [];
              const red = stepState[s.key] === 'attention' || gapsHere.length > 0;
              const st = red ? 'attention' : active ? (stepState[s.key] === 'done' ? 'done' : 'current') : stepState[s.key];
              return (
                <Box key={s.key} component="button" onClick={() => goto(s.key)} aria-current={active ? 'step' : undefined}
                  aria-label={gapsHere.length ? `${s.title}: ${gapsHere.length} still to fill in` : undefined}
                  sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: { md: '100%' }, flexShrink: 0, textAlign: 'left', cursor: 'pointer', borderRadius: '10px', p: 1.25,
                    border: `1px solid ${red ? '#fca5a5' : 'transparent'}`,
                    bgcolor: red ? '#fef2f2' : active ? '#e6f4f2' : 'transparent',
                    boxShadow: active ? `inset 3px 0 0 ${red ? RED : TEAL_DEEP}` : 'none',
                    '&:hover': { bgcolor: red ? '#fee2e2' : active ? '#e6f4f2' : '#f6f9f8' }, fontFamily: 'inherit' }}>
                  <StepIcon state={st} n={i + 1} />
                  <Box sx={{ display: { xs: active ? 'block' : 'none', md: 'block' } }}>
                    <Typography sx={{ fontWeight: 700, color: st === 'attention' ? RED : INK, fontSize: 15, whiteSpace: { xs: 'nowrap', md: 'normal' } }}>{s.title}</Typography>
                    {!narrow && <Typography sx={{ fontSize: 13, color: text.secondary, lineHeight: 1.35 }}>{s.blurb}</Typography>}
                    {!narrow && gapsHere.length > 0 && <Typography sx={{ fontSize: 12.5, color: RED, fontWeight: 600, mt: 0.25 }}>Not complete: {gapsHere.join(', ')}</Typography>}
                    {!narrow && !gapsHere.length && stepState[s.key] === 'attention' && <Typography sx={{ fontSize: 12.5, color: RED, fontWeight: 600, mt: 0.25 }}>Needs fixing</Typography>}
                    {s.key === 'ownership' && !narrow && !red && <Typography sx={{ fontSize: 12.5, color: AMBER, fontWeight: 600, mt: 0.25 }}>Includes the owner’s mandate to sell</Typography>}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: `1px solid ${EDGE}`, p: { xs: 2.5, md: 3.5 }, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: text.secondary }}>Step {stepIdx + 1} of {STEPS.length}</Typography>
            <Typography component="h2" sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: INK, mb: 0.5 }}>{current.title}</Typography>
            <Typography sx={{ color: text.secondary, mb: 3 }}>{current.blurb}</Typography>

            {step === 'property' && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField select fullWidth label="How the seller holds it" value={form.tenureType} onChange={set('tenureType')} disabled={!editable}
                      helperText={form.tenureType === 'cession' ? 'No title deed yet: rights from a council or a land developer. Eqaya checks them at that office.' : 'A title deed registered at the Deeds Office.'}>
                      <MenuItem value="title_deed">Title deed</MenuItem>
                      <MenuItem value="cession">Cession (no title deed yet)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth type="number" label="Stand size (m²)" value={form.standSizeSqm} onChange={set('standSizeSqm')} disabled={!detailsEditable} /></Grid>
                  {form.tenureType === 'cession' && (
                    <>
                      <Grid item xs={12} md={6}><TextField fullWidth required label="Stand number (as on the cession register)" value={form.cessionStandNumber} onChange={set('cessionStandNumber')} disabled={!detailsEditable} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth required label="Township" value={form.cessionTownship} onChange={set('cessionTownship')} disabled={!detailsEditable} /></Grid>
                    </>
                  )}
                  <Grid item xs={12}><TextField fullWidth label="Street address" value={form.location.address} onChange={setLoc('address')} disabled={!detailsEditable} helperText="Only shown to a buyer after they enquire." /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Suburb" value={form.location.suburb} onChange={setLoc('suburb')} disabled={!detailsEditable} /></Grid>
                  <Grid item xs={6} md={4}><TextField fullWidth label="City" value={form.location.city} onChange={setLoc('city')} disabled={!detailsEditable} /></Grid>
                  <Grid item xs={6} md={4}><TextField fullWidth label="Province" value={form.location.province} onChange={setLoc('province')} disabled={!detailsEditable} /></Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                  <HomeDetailsFields listingType={listing.listing_type} value={form} onChange={(v) => setForm(v)} disabled={!detailsEditable} sections={['pin']} />
                </Box>
                {saveBar()}
              </>
            )}

            {current.items && (
              <>
                {step === 'ownership' && (
                  <Alert severity="info" icon={false} sx={{ mb: 2, bgcolor: '#fdf2e9', color: INK, borderRadius: '10px' }}>
                    {form.tenureType === 'cession'
                      ? 'For a cession, Eqaya checks the rights at the council or developer that keeps the register. '
                      : 'Eqaya searches the title deed at the Deeds Office. '}
                    You may only market a property the owner has instructed you to sell: upload the signed mandate to sell, from the owner on record.
                  </Alert>
                )}
                {live && step === 'ownership' && <Alert severity="info" sx={{ mb: 2 }}>This listing is past desk review. The one item you can still update is the owner&apos;s mandate to sell, when it is renewed.</Alert>}
                {!editable && !live && <Alert severity="info" sx={{ mb: 2 }}>Eqaya is reviewing this listing, so the checklist is locked.</Alert>}
                <DossierAccordion listing={listing} keys={current.items(form.tenureType)} expanded editable={editable}
                  editableKeys={live ? ['mandate_to_sell'] : []} onSaveRequirement={saveRequirement} onUploadDocument={uploadDocument} onDeleteDocument={deleteDocument} />
                {nextBar}
              </>
            )}

            {step === 'buyers' && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={live ? 8 : 12}><TextField fullWidth label="Title" value={form.title} onChange={set('title')} disabled={!detailsEditable} inputProps={{ maxLength: 160 }} /></Grid>
                  {live && <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Asking price (USD)" value={form.priceUsd} onChange={set('priceUsd')} disabled={!detailsEditable} helperText="A change over 10% needs a new verification." /></Grid>}
                  <Grid item xs={12}><TextField fullWidth multiline minRows={4} label="Description for buyers" value={form.description} onChange={set('description')} disabled={!detailsEditable} /></Grid>
                </Grid>
                {!live && <Typography variant="body2" sx={{ color: text.secondary, mt: 1.5 }}>The asking price comes from the valuation in “Conveyancer and price”.</Typography>}
                <Typography sx={{ fontWeight: 700, color: INK, mt: 3, mb: 1 }}>{isHouse ? 'Home details' : 'Stand details'}</Typography>
                <HomeDetailsFields listingType={listing.listing_type} value={form} onChange={(v) => setForm(v)} disabled={!detailsEditable} sections={['facts']} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: INK }}>Photos</Typography>
                  {detailsEditable && <Button component="label" startIcon={<AddPhotoAlternate />} variant="outlined" disabled={photoProgress !== null} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>Add photos<input hidden type="file" accept="image/*" multiple onChange={uploadPhotos} /></Button>}
                </Stack>
                {photoProgress !== null && <LinearProgress variant="determinate" value={photoProgress} sx={{ mb: 1 }} />}
                {listing.photos.length === 0 ? <Typography variant="body2" sx={{ color: text.tertiary }}>No photos yet. Buyers see the first photo as the cover.</Typography> : (
                  <ImageList cols={narrow ? 2 : 4} gap={8}>{listing.photos.map((p) => <ImageListItem key={p.url}><img src={p.url} alt={p.caption || listing.title} loading="lazy" style={{ borderRadius: 8 }} /></ImageListItem>)}</ImageList>
                )}
                {saveBar('Save and review')}
              </>
            )}

            {step === 'review' && (
              <>
                {VERIFICATION_STATUSES.includes(listing.status) && <Box sx={{ mb: 3 }}><VerificationPanel listing={listing} onChanged={load} /></Box>}
                {editable && (
                  <>
                    {(() => {
                      const todo = [
                        ...gaps.property.map((g) => ({ label: g, step: 'property' })),
                        ...blockingItems.map((b) => ({ label: b.label, step: b.step, flagged: dossierItems.find((i) => i.key === b.key)?.status === 'flagged' })),
                        ...gaps.buyers.map((g) => ({ label: g, step: 'buyers' })),
                      ];
                      return todo.length ? (
                        <>
                          <Typography sx={{ fontWeight: 700, color: INK, mb: 1 }}>Still to do before you can submit</Typography>
                          <Stack spacing={0.75} sx={{ mb: 3 }}>
                            {todo.map((t) => (
                              <Stack key={`${t.step}-${t.label}`} direction="row" spacing={1.25} alignItems="center" sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#fef2f2', border: '1px solid #fca5a5' }}>
                                {t.flagged ? <ErrorOutline sx={{ color: AMBER }} /> : <RadioButtonUnchecked sx={{ color: '#94a3b8' }} />}
                                <Typography sx={{ flex: 1, color: INK }}>{t.label}{t.flagged ? ' (Eqaya asked for more)' : ''}</Typography>
                                {t.step && <Button size="small" onClick={() => goto(t.step)} sx={{ textTransform: 'none', fontWeight: 700 }}>{STEPS.find((s) => s.key === t.step)?.title} →</Button>}
                              </Stack>
                            ))}
                          </Stack>
                        </>
                      ) : (
                        <Alert severity="success" sx={{ mb: 3 }}>Everything is filled in. Submit it and Eqaya reviews the dossier, then arranges the verification visit.</Alert>
                      );
                    })()}
                    <Button variant="contained" startIcon={<Send />} onClick={() => setConfirm('submit')}
                      sx={{ height: 50, px: 3.5, borderRadius: '10px', bgcolor: brand.primaryDark, textTransform: 'none', fontWeight: 700, fontSize: 16, boxShadow: 'none', '&:hover': { bgcolor: TEAL_DEEP } }}>
                      Submit for desk review
                    </Button>
                  </>
                )}
                {listing.status === 'submitted' && <Alert severity="info">Eqaya is reviewing the dossier. You will get a message when it is done.</Alert>}
                {live && <Typography variant="body2" sx={{ color: text.secondary, mt: 2 }}>{standingBlocked ? STANDING_HINT : 'You can still change the title, description, home details, photos and asking price in “What buyers see”. A price change over 10%, or a change to the stand size or stand number, hides the listing until it is re-verified.'}</Typography>}
              </>
            )}
          </Box>
        </Box>
      </Container>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm === 'submit' ? 'Submit for desk review?' : 'Withdraw this listing?'}</DialogTitle>
        <DialogContent><Typography>{confirm === 'submit' ? 'The checklist locks while Eqaya reviews it. You can withdraw to make changes.' : 'A withdrawn listing cannot be reopened; you would create a new one.'}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setConfirm(null)}>Cancel</Button><Button variant="contained" color={confirm === 'submit' ? 'primary' : 'error'} onClick={runConfirmed}>{confirm === 'submit' ? 'Submit' : 'Withdraw'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default RealtorListingEditorPage;
